/**
 * Formatação de data e hora — no fuso do ESCRITÓRIO (TUT-138a).
 *
 * **O problema que isto resolve.** Até aqui os formatadores usavam o fuso do
 * NAVEGADOR. Para um escritório inteiro em Brasília não fazia diferença; para
 * um escritório em Manaus, todo horário aparecia uma hora adiantado — e, pior,
 * um operador viajando via os horários MUDAREM ao trocar de estado, sem nada
 * ter mudado nos dados.
 *
 * **A regra: guardar instante, renderizar local.** O que chega da API é sempre
 * um instante absoluto (ISO, em UTC). O fuso é escolha do escritório e governa
 * só a leitura.
 *
 * **Por que o fuso é do escritório e não do navegador:** o que importa é o
 * expediente de quem trabalha no processo, não onde a pessoa está sentada hoje.
 * Prazo processual é do foro, não do turista.
 */

// O fuso ativo, definido UMA vez quando a sessão chega. O padrão vale só até
// lá — e é o mesmo padrão do catálogo, para a primeira pintura não mostrar um
// horário que muda logo depois.
let fusoDoEscritorio = "America/Sao_Paulo";

const ouvintesDoFuso = new Set<() => void>();

/** Chamado quando a sessão chega, com o fuso que veio junto dela. */
export function definirFusoDoEscritorio(fuso: string | undefined): void {
  if (!fuso || fuso === fusoDoEscritorio) return;
  fusoDoEscritorio = fuso;
  for (const ouvir of ouvintesDoFuso) ouvir();
}

/**
 * TUT-472 — quem desenha o fuso na tela (o relógio do escritório) assina a
 * troca: salvar outro fuso em Configurações não remonta nada, e o relógio
 * seguia mostrando a cidade antiga. Forma de `useSyncExternalStore`.
 */
export function assinarFusoDoEscritorio(ouvir: () => void): () => void {
  ouvintesDoFuso.add(ouvir);
  return () => {
    ouvintesDoFuso.delete(ouvir);
  };
}

export function fusoAtivo(): string {
  return fusoDoEscritorio;
}

/**
 * TUT-393 — texto `AAAA-MM-DD` é DATA DE CALENDÁRIO (coluna `date`: a
 * distribuição do processo, o vencimento do boleto, o período do relatório),
 * nunca um instante. `new Date("2026-08-01")` é meia-noite UTC, e no fuso de
 * Brasília isso é 31/07. Os formatadores abaixo reconhecem esse formato e
 * trocam os pedaços de lugar, em vez de passar por `Date` — quem chama não
 * precisa lembrar qual das duas funções usar.
 */
const DATA_DE_CALENDARIO = /^\d{4}-\d{2}-\d{2}$/;

function ehDataDeCalendario(value: string | Date): value is string {
  return typeof value === "string" && DATA_DE_CALENDARIO.test(value);
}

/**
 * Chave de calendário no fuso do escritório (`AAAA-MM-DD`).
 *
 * Comparar o timestamp cru ou usar `getDate()` classificaria "hoje" no fuso
 * do navegador. Prazo e perícia seguem o expediente do escritório, inclusive
 * quando o operador está viajando.
 */
export function calendarDateKey(
  value: string | Date,
  fuso: string = fusoDoEscritorio,
): string {
  if (ehDataDeCalendario(value)) return value;
  const parts = formatador(
    "calendarKey",
    { year: "numeric", month: "2-digit", day: "2-digit" },
    fuso,
  ).formatToParts(typeof value === "string" ? new Date(value) : value);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? "";

  return `${part("year")}-${part("month")}-${part("day")}`;
}

/**
 * TUT-410 — o `<input type="datetime-local">` fala `AAAA-MM-DDTHH:mm` SEM
 * fuso, e `new Date(valor)` o lê no fuso do NAVEGADOR. Quem marcava a perícia
 * de Manaus "às 14:00" num notebook em Brasília gravava 17:00 UTC — 13:00 em
 * Manaus. Estas duas funções são a ida e a volta no fuso certo: o do
 * escritório por padrão, ou o da perícia quando ela tem um.
 */
export function isoParaCampoDataHora(
  valor: string | null | undefined,
  fuso: string = fusoDoEscritorio,
): string {
  if (!valor) return "";
  const instante = new Date(valor);
  if (Number.isNaN(instante.getTime())) return "";
  const p = partesNoFuso(instante, fuso);
  return `${p.ano}-${dois(p.mes)}-${dois(p.dia)}T${dois(p.hora)}:${dois(p.minuto)}`;
}

/** A volta: `AAAA-MM-DDTHH:mm` lido no fuso → instante ISO; `null` se inválido. */
export function campoDataHoraParaIso(
  valor: string,
  fuso: string = fusoDoEscritorio,
): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/u.exec(valor);
  if (!m) return null;
  const [ano, mes, dia, hora, minuto] = m.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ];
  const comoUtc = Date.UTC(ano, mes - 1, dia, hora, minuto);
  // Duas voltas: a primeira acha o deslocamento perto do instante; a segunda
  // corrige se o palpite caiu do outro lado de uma mudança de horário.
  let instante = comoUtc - deslocamento(new Date(comoUtc), fuso);
  instante = comoUtc - deslocamento(new Date(instante), fuso);
  return new Date(instante).toISOString();
}

function dois(n: number): string {
  return String(n).padStart(2, "0");
}

function partesNoFuso(instante: Date, fuso: string) {
  const partes = formatador(
    "partes",
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    },
    fuso,
  ).formatToParts(instante);
  const n = (tipo: Intl.DateTimeFormatPartTypes) =>
    Number(partes.find((parte) => parte.type === tipo)?.value ?? "0");
  return {
    ano: n("year"),
    mes: n("month"),
    dia: n("day"),
    hora: n("hour") % 24,
    minuto: n("minute"),
    segundo: n("second"),
  };
}

/** Quanto o relógio do fuso está à frente do UTC no instante dado, em ms. */
function deslocamento(instante: Date, fuso: string): number {
  const p = partesNoFuso(instante, fuso);
  const comoUtc = Date.UTC(p.ano, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo);
  return comoUtc - Math.floor(instante.getTime() / 1_000) * 1_000;
}

// Os formatadores são memorizados por fuso: construir um `Intl.DateTimeFormat`
// é caro o bastante para importar numa lista de mil linhas, e o fuso muda no
// máximo uma vez por sessão.
const cache = new Map<string, Intl.DateTimeFormat>();

// O fuso entra por PARÂMETRO, com o do escritório como padrão. Antes ele era
// lido direto da variável aqui dentro, e a auditoria da TUT-138b precisa
// formatar no fuso que veio com a LINHA — que não é o de hoje. Deixar o fuso
// implícito faria a chave do cache mentir: dois fusos diferentes cairiam no
// mesmo formatador, e o segundo leria no fuso do primeiro.
function formatador(
  chave: string,
  opcoes: Intl.DateTimeFormatOptions,
  fuso: string = fusoDoEscritorio,
): Intl.DateTimeFormat {
  const id = `${chave}@${fuso}`;
  const existente = cache.get(id);
  if (existente) return existente;
  const criado = new Intl.DateTimeFormat("pt-BR", {
    ...opcoes,
    timeZone: fuso,
  });
  cache.set(id, criado);
  return criado;
}

const OPCOES_DATA: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};

const OPCOES_DATA_HORA: Intl.DateTimeFormatOptions = {
  ...OPCOES_DATA,
  hour: "2-digit",
  minute: "2-digit",
};

const OPCOES_COMPACTA: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
};

export function formatDate(value: string): string {
  if (ehDataDeCalendario(value)) return formatCalendarDate(value);
  return formatador("data", OPCOES_DATA).format(new Date(value));
}

export function formatDateTime(value: string): string {
  return formatador("dataHora", OPCOES_DATA_HORA).format(new Date(value)).replace(",", "");
}

/**
 * Data e hora no fuso QUE VEIO COM O REGISTRO (TUT-138b).
 *
 * `formatDateTime` lê no fuso que o escritório usa AGORA, e é o certo para
 * prazo, agenda e perícia: essas coisas são planejadas no expediente de hoje.
 * A auditoria é o oposto — ela conta o que já aconteceu, e ler um evento antigo
 * no fuso de hoje reescreve o passado em silêncio.
 *
 * O caso que dói é o da DATA, não o da hora: um registro feito às 23:40 de
 * Manaus vira 00:40 do dia seguinte em Brasília. Quem for reconstituir "o que
 * foi feito no dia 30" encontra o evento no dia 31, e prazo processual se conta
 * por data.
 *
 * **Fuso nulo é evento anterior à migration 0059, e a chamada devolve `null`
 * de propósito.** A tentação seria cair no fuso de hoje — e aí o horário
 * presumido teria exatamente a mesma cara do horário sabido, que é o pior
 * resultado possível para uma trilha de auditoria. Quem chama decide como dizer
 * "não registrado"; o que não pode é fingir que sabe.
 */
export function formatDateTimeNoFuso(
  value: string,
  fuso: string | null,
): string | null {
  if (!fuso) return null;
  return formatador("dataHora", OPCOES_DATA_HORA, fuso)
    .format(new Date(value))
    .replace(",", "");
}

/**
 * Só a hora (`HH:mm`), no fuso pedido ou no do escritório (TUT-138c).
 *
 * Serve ao relógio da Visão Geral e das Configurações — que existe para
 * responder "que horas são NO ESCRITÓRIO", pergunta que o relógio do sistema
 * operacional não responde quando quem opera está em outro fuso.
 */
export function formatClock(value: Date, fuso: string = fusoDoEscritorio): string {
  return formatador("hora", { hour: "2-digit", minute: "2-digit" }, fuso).format(
    value,
  );
}

/**
 * `AAAA-MM-DD` para `DD/MM/AAAA`, **sem passar por `Date`**.
 *
 * `new Date("2026-08-01")` é meia-noite UTC e, em fuso negativo, renderiza
 * 31/07 — o defeito que a TUT-144 fatia 1e já pagou com vigência de norma. Data
 * de calendário (diária de locação, vigência de contato) é TEXTO com três
 * pedaços, não um instante: o que se troca de lugar são os pedaços.
 *
 * Morava em `lib/rentals.ts`, onde nasceu. A TUT-205c a trouxe para cá porque a
 * vigência de contato precisa exatamente dela, e um editor de contatos
 * importando o vocabulário da locação para formatar uma data seria a primeira
 * linha de uma dependência que ninguém consegue explicar depois. `rentals.ts`
 * reexporta o nome para não quebrar quem já chamava de lá.
 */
export function formatCalendarDate(value: string): string {
  const [ano, mes, dia] = value.split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : value;
}

/**
 * TUT-495 — o dia da semana por extenso ("sexta-feira") de uma data de
 * calendário `AAAA-MM-DD`, lida em meio-dia UTC: o mesmo dia em qualquer fuso.
 */
export function diaDaSemanaPorExtenso(data: string): string {
  return formatador("diaSemanaExtenso", { weekday: "long" }, "UTC").format(
    new Date(`${data}T12:00:00Z`),
  );
}

export function formatCompactDate(value: string): {
  day: string;
  month: string;
  weekday: string;
} {
  // Data de calendário vira meio-dia UTC lida em UTC: o mesmo dia em
  // qualquer fuso (TUT-393).
  const calendario = ehDataDeCalendario(value);
  const date = new Date(calendario ? `${value}T12:00:00Z` : value);
  const fuso = calendario ? "UTC" : fusoDoEscritorio;
  const compact = formatador("compacta", OPCOES_COMPACTA, fuso).formatToParts(date);
  return {
    day: compact.find((part) => part.type === "day")?.value ?? "",
    month: (compact.find((part) => part.type === "month")?.value ?? "")
      .replace(".", "")
      .toUpperCase(),
    weekday: formatador("diaSemana", { weekday: "short" }, fuso)
      .format(date)
      .replace(".", ""),
  };
}

export function formatCurrency(value: number | null): string {
  if (value === null) return "Não informado";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

/**
 * O ponto que fecha uma frase terminada por um nome (TUT-472).
 *
 * Nome de empresa termina em ponto com frequência ("Ltda.", "S.A."), e o
 * ponto fixo depois dele saía dobrado: "fundida em Indústria Horizonte
 * Ltda.. Dá para desfazer". Devolve "" quando o nome já fecha a frase.
 */
export function pontoFinalApos(nome: string): string {
  return nome.trimEnd().endsWith(".") ? "" : ".";
}

/**
 * Maiúscula SÓ na primeira letra: "outubro de 2026" vira "Outubro de 2026".
 * A classe `capitalize` do CSS põe maiúscula em CADA palavra ("Outubro De
 * 2026"), e o `Intl` de pt-BR devolve mês e dia da semana em minúscula
 * (TUT-472: era a mesma linha copiada em quatro lugares).
 */
export function primeiraMaiuscula(texto: string): string {
  return texto.charAt(0).toLocaleUpperCase("pt-BR") + texto.slice(1);
}

/**
 * Tamanho de arquivo em unidade legível (TUT-169).
 *
 * Estava escrita à mão dentro do `PortalDocumentsSection`; subiu para cá quando
 * o seletor de arquivo passou a dizer o TAMANHO do que foi escolhido — duas
 * cópias divergiriam no dia em que alguém trocasse a casa decimal, e o mesmo
 * arquivo teria dois tamanhos em duas telas do mesmo produto.
 *
 * Base 1000 (KB, não KiB) de propósito: é o que o sistema operacional mostra
 * para quem escolheu o arquivo, e o limite do produto é anunciado como "25 MB".
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1_000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${formatDecimal(bytes / 1_000, 1)} KB`;
  if (bytes < 1_000_000_000) return `${formatDecimal(bytes / 1_000_000, 1)} MB`;
  return `${formatDecimal(bytes / 1_000_000_000, 2)} GB`;
}

/**
 * TUT-472 — número com casas FIXAS na escrita brasileira: "824,3", e não o
 * "824.3" do `toFixed`, que não conhece localidade. Tamanho de arquivo e de
 * disco saía com ponto em telas onde todo o resto do app escreve "42,7 min".
 */
export function formatDecimal(valor: number, casas: number): string {
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function meiaNoiteUtc(chave: string): number {
  const [ano, mes, dia] = chave.split("-").map(Number);
  return Date.UTC(ano!, mes! - 1, dia!);
}

/**
 * Quantos DIAS DE CALENDÁRIO, no fuso do escritório, separam hoje de `value`
 * (negativo = passado). TUT-393: a conta antiga era `Math.ceil` sobre a
 * diferença de instantes, e errava nas duas pontas — o prazo de HOJE às 20h,
 * visto às 8h, está a 12 horas e o `ceil` dava 1: "Amanhã"; o de ONTEM às 20h,
 * visto hoje às 8h, está a -12 horas, o `ceil` dava 0, e o vencido virava "Hoje".
 */
export function diasDeCalendarioAte(
  value: string | Date,
  agora: Date | number = Date.now(),
  fuso: string = fusoDoEscritorio,
): number {
  const alvo = meiaNoiteUtc(calendarDateKey(value, fuso));
  const hoje = meiaNoiteUtc(calendarDateKey(new Date(agora), fuso));
  return Math.round((alvo - hoje) / 86_400_000);
}

/** O instante já passou? Data de calendário só "passa" quando o dia acaba. */
export function jaPassou(value: string, agora: number = Date.now()): boolean {
  return ehDataDeCalendario(value)
    ? diasDeCalendarioAte(value, agora) < 0
    : new Date(value).getTime() < agora;
}

export function relativeLabel(value: string, agora: number = Date.now()): string {
  const days = diasDeCalendarioAte(value, agora);
  if (days === 0) return "Hoje";
  if (days === 1) return "Amanhã";
  if (days > 1) return `em ${days} dias`;
  if (days === -1) return "ontem";
  return `há ${Math.abs(days)} dias`;
}
